import React, { useEffect, useState } from "react";
import {
    IconButton,
    Modal,
    Box,
    TextField,
    Button,
    Typography,
    Grid,
    Alert,
    AlertTitle,
    CircularProgress,
    Paper,
    Fade,
    Slide,
    useTheme,
    useMediaQuery,
    Divider,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Avatar,
    alpha,
    Switch,
    FormControlLabel,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import CustomSelect from "../../UI/CustomSelect";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import CloseIcon from "@mui/icons-material/Close";
import {
    getAllCorporateUsers,
    addCorporateUser,
    updateCorporateUser,
    deleteCorporateUser,
    getCorporateUsersByProgramCode,
} from '../CorporateQueries';
import { getProgramCodesFromClients } from "../../Queries";
import UserTable from "../../UserManagement/UserTable";
import LoadingScreen from "../../UI/LoadingScreen";
import { useNavigate } from "react-router-dom";

const CorporateUserManagement = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Form styles
    const formStyles = {
        textField: {
            '& .MuiOutlinedInput-root': {
                fontFamily: theme.typography.fontFamily,
                fontSize: '14px',
                borderRadius: '4px',
                backgroundColor: '#fff',
                height: '40px',
                '& fieldset': {
                    borderColor: '#E0E0E0',
                    borderRadius: '4px',
                },
                '&:hover fieldset': {
                    borderColor: '#9AA6AC'
                },
                '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: '1px'
                }
            },
            '& .MuiInputLabel-root': {
                fontFamily: theme.typography.fontFamily,
                fontSize: '14px',
                color: theme.palette.text.primary,
                transform: 'translate(14px, 12px)',
                '&.MuiInputLabel-shrink': {
                    padding: '0 4px',
                    transform: 'translate(14px, -9px) scale(0.75)',
                }
            },
            '& .MuiInputBase-input': {
                fontFamily: theme.typography.fontFamily,
                fontSize: '14px',
                padding: '8px 14px',
                height: '24px'
            }
        },
    };

    const [open, setOpen] = useState(false);
    const [editUser, setEditUser] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [alert, setAlert] = useState({
        open: false,
        severity: "success",
        message: "",
    });
    const [tableData, setTableData] = useState([]);
    const [registrationCodes, setRegistrationCodes] = useState([]);
    const [programCodeMenuOpen, setProgramCodeMenuOpen] = useState(false);
    const [managerList, setManagerList] = useState([]);

    const [registerData, setRegisterData] = useState({
        email: "",
        first_name: "",
        last_name: "",
        employee_id: "",
        date_of_joining: "",
        job_title: "",
        department: "",
        role: "Employee",
        internal_experience: "",
        reporting_l1: "",
        reporting_l2: "",
        work_location: "",
        company: "",
        notes: "",
        profile_status: "Active",
    });

    const handleCloseAlert = () => {
        setAlert({
            ...alert,
            open: false,
        });

        setRegisterData({
            email: "",
            first_name: "",
            last_name: "",
            employee_id: "",
            date_of_joining: "",
            job_title: "",
            department: "",
            internal_experience: "",
            reporting_l1: "",
            reporting_l2: "",
            work_location: "",
            company: "",
            notes: "",
            profile_status: "Active",
        });
        setOpen(false);
    };

    const handleSuccess = (message) => {
        setError(null);
        setAlert({
            open: true,
            severity: "success",
            message,
        });
        setLoading(false);
        // Don't close modal - show success message in the same dialog
    };

    const handleError = (message) => {
        setError(message);
        setAlert({
            open: true,
            severity: "error",
            message,
        });
        setLoading(false);
    };

    const handleChange = (event) => {
        setRegisterData({
            ...registerData,
            [event.target.name]: event.target.value,
        });
    };

    const handleEdit = (data) => {
        setEditUser(true);
        setError(null);
        setAlert({
            open: false,
            severity: "success",
            message: "",
        });
        setRegisterData({
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
            employee_id: data.employee_id,
            date_of_joining: data.date_of_joining,
            job_title: data.job_title,
            department: data.department,
            role: data.role || "Employee",
            internal_experience: data.internal_experience,
            reporting_l1: data.reporting_l1,
            reporting_l2: data.reporting_l2,
            work_location: data.work_location,
            company: data.company,
            notes: data.notes,
            profile_status: data.profile_status,
        });
        setOpen(true);
    };

    const handleDeleteConfirmation = (email, userData) => {
        setUserToDelete({ email, ...userData });
        setDeleteDialogOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setUserToDelete(null);
    };

    const handleDeleteConfirm = async () => {
        if (userToDelete) {
            await handleDelete(userToDelete.email);
        }
        setDeleteDialogOpen(false);
        setUserToDelete(null);
    };

    const handleDelete = async (email) => {
        setLoading(true);
        try {
            const response = await deleteCorporateUser(email);
            if (response && response.data) {
                // Check if response has statusCode (old format) or direct message (new format)
                if (response.data.statusCode) {
                    // Old format with statusCode and body
                    const statusCode = response.data.statusCode;
                    const body = typeof response.data.body === 'string' 
                        ? JSON.parse(response.data.body) 
                        : response.data.body;
                    
                    if (statusCode === 200) {
                        console.log('Deleted user:', body.user);
                        const updatedUsers = await getAllCorporateUsers();
                        setTableData(updatedUsers);
                        handleSuccess(body.message || "User deleted successfully");
                    } else {
                        handleError(body.message || "Unable to delete the user. Please try again.");
                    }
                } else if (response.data.message) {
                    // New format with direct message and user data
                    console.log('Deleted user:', response.data.user);
                    const updatedUsers = await getAllCorporateUsers();
                    setTableData(updatedUsers);
                    handleSuccess(response.data.message);
                } else {
                    handleError("Unable to delete the user. Please try again.");
                }
            } else {
                handleError("Unable to delete the user. Please try again.");
            }
        } catch (error) {
            console.error("Delete error:", error);
            handleError("Unable to delete the user. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleUser = async (event) => {
        event.preventDefault();
        setLoading(true);
        try {
            const userEmail = localStorage.getItem('userEmail') || sessionStorage.getItem('userEmail') || '';
            const dataToSubmit = {
                ...registerData,
                program_code: process.env.REACT_APP_CORPORATE_CODE || '',
                created_by: userEmail,
                updated_by: userEmail,
                // Add required fields for AdminCreateUser
                phone: registerData.phone || "",
                dob: registerData.dob || "",
                qualification: registerData.qualification || "",
                graduationYear: registerData.graduationYear || "",
                course: registerData.course || "",
                batch: registerData.batch || "",
            };

            let response;
            if (editUser) {
                response = await updateCorporateUser(dataToSubmit);
            } else {
                response = await addCorporateUser(dataToSubmit);
            }

            // Handle response - the response is directly the data object
            if (response && response.data) {
                // Check if response has statusCode (old format) or direct message (new format)
                if (response.data.statusCode) {
                    // Old format with statusCode and body
                    const statusCode = response.data.statusCode;
                    const body = typeof response.data.body === 'string' 
                        ? JSON.parse(response.data.body) 
                        : response.data.body;
                    
                    if (statusCode === 200) {
                        console.log('User data from API:', body.user);
                        await getTableData();
                        handleSuccess(body.message || (editUser ? "User updated successfully" : "User created successfully"));
                    } else {
                        setLoading(false);
                        handleError(body.message || "Unable to save the user. Please try again.");
                    }
                } else if (response.data.message) {
                    // New format with direct message and user data
                    console.log('User data from API:', response.data.user);
                    await getTableData();
                    handleSuccess(response.data.message);
                } else {
                    setLoading(false);
                    handleError("Unable to save the user. Please try again.");
                }
            } else {
                setLoading(false);
                handleError("Unable to save the user. Please try again.");
            }
        } catch (error) {
            setLoading(false);
            handleError("Unable to save the user. Please try again.");
        }
    };

    const handleOpenModal = () => {
        setRegisterData({
            email: "",
            first_name: "",
            last_name: "",
            employee_id: "",
            date_of_joining: "",
            job_title: "",
            department: "",
            role: "Employee",
            internal_experience: "",
            reporting_l1: "",
            reporting_l2: "",
            work_location: "",
            company: "",
            notes: "",
            profile_status: "Active",
        });
        setEditUser(false);
        setError(null);
        setAlert({
            open: false,
            severity: "success",
            message: "",
        });
        setOpen(true);
        setProgramCodeMenuOpen(false);
    };

    const handleCloseModal = () => {
        setOpen(false);
        setError(null);
        setAlert({
            open: false,
            severity: "success",
            message: "",
        });
        setProgramCodeMenuOpen(false);
    };

    const navigate = useNavigate();

    const getTableData = async () => {
        try {
            const users = await getAllCorporateUsers();
            setTableData(users);
            // Filter managers for reporting dropdowns (Team Lead, Project Manager, Program Manager, HR)
            const managers = users.filter(user => 
                user.role && ['Team Lead', 'Project Manager', 'Program Manager', 'HR'].includes(user.role)
            ).map(user => ({
                value: user.email,
                label: `${user.name} (${user.role})`
            }));
            setManagerList(managers);
            setIsLoading(false);
        } catch (error) {
            console.log('An error occurred while fetching corporate users.', error);
            setError('Failed to load corporate users. Please ensure you are signed in.');
        }
    };

    const getDropDowns = async () => {
        try {
            const programCodes = await getProgramCodesFromClients();
            setRegistrationCodes(programCodes);
        } catch (error) {
            console.log('An error occurred while fetching program codes.', error);
        }
    };

    useEffect(() => {
        getDropDowns();
        setIsLoading(true);
        getTableData();
    }, []);

    return (
        <div style={{ height: '100%', background: 'white' }}>
            {/* Header Section */}
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Typography
                    variant="h6"
                    component="h1"
                    gutterBottom={false}
                    sx={{ 
                        fontSize: '16px',
                        fontFamily: theme.typography.fontFamily,
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                    }}
                >
                    User Management
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ 
                        fontFamily: theme.typography.fontFamily,
                        color: theme.palette.text.secondary,
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: 1.5,
                    }}
                >
                    This is an admin screen to manage the users and the reporting structure.
                </Typography>
            </Box>

            <Divider />

            {/* Table Section */}
            <div
                style={{
                    height: 'auto',
                    maxHeight: isMobile ? '70vh' : 'none',
                    width: "100%",
                    overflow: isMobile ? 'visible' : 'hidden',
                    background: 'white',
                    marginTop: "8px"
                }}
            >
                {!isLoading ? (
                    <UserTable
                        tableData={tableData}
                        handleEdit={handleEdit}
                        handleDelete={handleDeleteConfirmation}
                        isLoading={isLoading}
                        corporateMode={true}
                        actionButtons={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={handleOpenModal}
                                    startIcon={<PersonAddAltIcon sx={{ fontSize: 14 }} />}
                                    size="small"
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 500,
                                        borderRadius: 1,
                                        px: 2,
                                        fontSize: '13px',
                                        py: 0.75,
                                        boxShadow: 'none'
                                    }}
                                >
                                    Add User
                                </Button>
                            </Stack>
                        }
                    />
                ) : (
                    <LoadingScreen message="Loading corporate users..." />
                )}
            </div>

            {/* User Modal */}
            <Modal
                open={open}
                onClose={handleCloseModal}
                closeAfterTransition
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Fade in={open}>
                    <Paper
                        elevation={24}
                        sx={{
                            width: isSmallMobile ? '95vw' : isMobile ? '90vw' : '80vw',
                            maxWidth: 700,
                            maxHeight: isSmallMobile ? '95vh' : '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: '12px',
                            backgroundColor: '#f8f9fa',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Modal Header */}
                        <Box
                            sx={{
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                p: 3,
                                flexShrink: 0,
                                zIndex: 2,
                            }}
                        >
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Typography 
                                    variant={isMobile ? "h6" : "h5"}
                                    component="div"
                                    fontWeight="bold"
                                    sx={{ color: theme.palette.primary.contrastText }}
                                >
                                    {editUser ? "Edit User" : "Add User"}
                                </Typography>
                                <IconButton
                                    onClick={handleCloseModal}
                                    sx={{
                                        color: 'white',
                                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                        </Box>

                        {/* Form wrapper - wraps both content and footer */}
                        <Box component="form" onSubmit={handleUser} sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
                            {/* Modal Content */}
                            <Box sx={{ 
                                p: 3, 
                                flexGrow: 1,
                                overflow: 'auto',
                            }}>
                            {loading && (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    flexDirection="column"
                                    gap={2}
                                    py={4}
                                >
                                    <CircularProgress 
                                        size={50} 
                                        sx={{
                                            '& .MuiCircularProgress-circle': {
                                                stroke: theme.palette.primary.main,
                                            },
                                        }}
                                    />
                                    <Typography 
                                        variant="body1" 
                                        color="text.secondary"
                                        sx={{
                                            fontSize: '0.9rem',
                                            fontWeight: 500
                                        }}
                                    >
                                        Processing...
                                    </Typography>
                                </Box>
                            )}

                            {alert.open && (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    flexDirection="column"
                                    gap={3}
                                    py={6}
                                >
                                    <Slide direction="down" in={alert.open}>
                                        <Alert
                                            severity={alert.severity}
                                            sx={{ 
                                                mb: 2, 
                                                borderRadius: 2,
                                                width: '100%',
                                                maxWidth: 500
                                            }}
                                        >
                                            <AlertTitle sx={{ fontWeight: 'bold' }}>
                                                {alert.severity === "success" ? "Success" : "Error"}
                                            </AlertTitle>
                                            {alert.message}
                                        </Alert>
                                    </Slide>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleCloseAlert}
                                        sx={{
                                            textTransform: "none",
                                            fontWeight: "bold",
                                            borderRadius: 2,
                                            px: 4,
                                            py: 1.5,
                                        }}
                                    >
                                        Close
                                    </Button>
                                </Box>
                            )}

                            {!loading && !alert.open && (
                                <Box sx={{ width: '100%' }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="First Name"
                                                name="first_name"
                                                value={registerData.first_name}
                                                onChange={handleChange}
                                                required
                                                fullWidth
                                                variant="outlined"
                                                sx={formStyles.textField}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Last Name"
                                                name="last_name"
                                                value={registerData.last_name}
                                                onChange={handleChange}
                                                required
                                                fullWidth
                                                variant="outlined"
                                                sx={formStyles.textField}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                label="Email Address"
                                                name="email"
                                                type="email"
                                                value={registerData.email}
                                                onChange={handleChange}
                                                required
                                                fullWidth
                                                variant="outlined"
                                                disabled={editUser}
                                                sx={formStyles.textField}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Job Title"
                                                name="job_title"
                                                value={registerData.job_title}
                                                onChange={handleChange}
                                                fullWidth
                                                variant="outlined"
                                                sx={formStyles.textField}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Team"
                                                name="department"
                                                value={registerData.department}
                                                onChange={handleChange}
                                                fullWidth
                                                variant="outlined"
                                                sx={formStyles.textField}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <CustomSelect
                                                label="Role"
                                                name="role"
                                                value={registerData.role}
                                                onChange={handleChange}
                                                required
                                                options={["Employee", "Team Lead", "Project Manager", "Program Manager", "HR"]}
                                                placeholder="Select Role"
                                            />
                                        </Grid>

                                        

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Employee ID"
                                                name="employee_id"
                                                value={registerData.employee_id}
                                                onChange={handleChange}
                                                required
                                                fullWidth
                                                variant="outlined"
                                                sx={formStyles.textField}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Date of Joining"
                                                name="date_of_joining"
                                                type="date"
                                                value={registerData.date_of_joining}
                                                onChange={handleChange}
                                                required
                                                fullWidth
                                                variant="outlined"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                sx={formStyles.textField}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Internal Experience (Years)"
                                                name="internal_experience"
                                                value={registerData.internal_experience}
                                                onChange={handleChange}
                                                fullWidth
                                                variant="outlined"
                                                sx={formStyles.textField}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <CustomSelect
                                                label="Reporting Manager (L1)"
                                                name="reporting_l1"
                                                value={registerData.reporting_l1}
                                                onChange={handleChange}
                                                options={managerList}
                                                placeholder="Select L1 Manager"
                                                required
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <CustomSelect
                                                label="Reporting Manager (L2)"
                                                name="reporting_l2"
                                                value={registerData.reporting_l2}
                                                onChange={handleChange}
                                                options={managerList}
                                                placeholder="Select L2 Manager"
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Work Location"
                                                name="work_location"
                                                value={registerData.work_location}
                                                onChange={handleChange}
                                                fullWidth
                                                variant="outlined"
                                                sx={formStyles.textField}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                label="Notes"
                                                name="notes"
                                                value={registerData.notes}
                                                onChange={handleChange}
                                                fullWidth
                                                variant="outlined"
                                                multiline
                                                rows={3}
                                                sx={{
                                                    ...formStyles.textField,
                                                    '& .MuiOutlinedInput-root': {
                                                        ...formStyles.textField['& .MuiOutlinedInput-root'],
                                                        height: 'auto'
                                                    }
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <Box sx={{ 
                                                    display: 'inline-flex', 
                                                    alignItems: 'center',
                                                    height: '40px',
                                                    border: '1px solid #E0E0E0',
                                                    borderRadius: '4px',
                                                    px: 2,
                                                    backgroundColor: '#fff'
                                                }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={registerData.profile_status === "Active"}
                                                                onChange={(e) => {
                                                                    setRegisterData({
                                                                        ...registerData,
                                                                        profile_status: e.target.checked ? "Active" : "Inactive"
                                                                    });
                                                                }}
                                                                sx={{
                                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                                        color: theme.palette.success.main,
                                                                    },
                                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                        backgroundColor: theme.palette.success.main,
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        label={
                                                            <Typography sx={{ 
                                                                fontSize: '14px',
                                                                fontFamily: theme.typography.fontFamily,
                                                                color: registerData.profile_status === "Active" 
                                                                    ? theme.palette.success.main 
                                                                    : theme.palette.text.secondary,
                                                                fontWeight: 500
                                                            }}>
                                                                {registerData.profile_status === "Active" ? "Active" : "Inactive"}
                                                            </Typography>
                                                        }
                                                        sx={{ m: 0 }}
                                                    />
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/* Action Buttons */}
                                </Box>
                            )}
                            {error && (
                                <Box sx={{ mt: 3 }}>
                                    <Alert severity="error">{error}</Alert>
                                </Box>
                            )}
                        </Box>

                        {/* Modal Footer - Fixed at bottom */}
                        {!loading && !alert.open && (
                            <Box
                                sx={{
                                    p: 3,
                                    pt: 2,
                                    borderTop: '1px solid #E0E0E0',
                                    backgroundColor: '#fff',
                                    flexShrink: 0,
                                    zIndex: 2,
                                }}
                            >
                                <Stack
                                        direction={isMobile ? "column" : "row"}
                                        spacing={2}
                                        justifyContent="flex-end"
                                        sx={{ 
                                            '& .MuiButton-root': {
                                                minWidth: isMobile ? '100%' : '140px',
                                                height: '36px',
                                                borderRadius: '8px'
                                            }
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            onClick={handleCloseModal}
                                            size="large"
                                            fullWidth={isMobile}
                                            sx={{
                                                textTransform: "none",
                                                fontWeight: "bold",
                                                borderRadius: 2,
                                                px: 4,
                                                py: 1.5,
                                                borderColor: theme.palette.primary.main,
                                                color: theme.palette.primary.main
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            fullWidth={isMobile}
                                            sx={{
                                                textTransform: "none",
                                                fontWeight: "bold",
                                                borderRadius: 2,
                                                px: 4,
                                                py: 1.5,
                                                boxShadow: 'none'
                                            }}
                                        >
                                            {editUser ? "Update User" : "Create User"}
                                        </Button>
                                    </Stack>
                            </Box>
                        )}
                        </Box>
                    </Paper>
                </Fade>
            </Modal>

            {/* Delete Confirmation Dialog */}
            <Dialog 
                open={deleteDialogOpen} 
                onClose={handleDeleteCancel}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        minWidth: 400
                    }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    color: theme.palette.error.main
                }}>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1) }}>
                        <WarningIcon sx={{ color: theme.palette.error.main }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
                            Confirm Deletion
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            This action cannot be undone
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ fontSize: '0.8rem' }}>
                        Are you sure you want to delete user <strong>{userToDelete?.first_name} {userToDelete?.last_name}</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.75rem' }}>
                        Email: {userToDelete?.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        All associated data will be permanently removed.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button 
                        onClick={handleDeleteCancel}
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        sx={{ borderRadius: 2 }}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={20} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CorporateUserManagement;
